import React from 'react'
import { useSelector } from 'react-redux'
import { MessageCircle } from 'lucide-react'
import { cn } from 'lib/utils'
import { selectUnreadChatCount } from 'slices/chatSlice'
import { useChatPopup } from 'features/Chat/ChatPopupContext'

/**
 * The Messages button in the top header bar.
 *
 * Reads only the popup context and one memoized Redux selector, deliberately
 * not `useChat()`: the chat context's value is rebuilt on every keystroke and
 * every incoming message, and the header must not re-render for either.
 */
const ChatPopupTrigger = ({ className = '' }: { className?: string }) => {
	const { isOpen, toggle } = useChatPopup()
	const unreadCount = useSelector(selectUnreadChatCount)

	return (
		<button
			type="button"
			onClick={toggle}
			data-testid="chat-popup-trigger"
			data-tour="nav-chat"
			aria-label={unreadCount > 0 ? `Messages, ${unreadCount} unread` : 'Messages'}
			aria-expanded={isOpen}
			title="Messages"
			className={cn(
				'relative inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors',
				'text-gray-500 hover:text-primary',
				'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0080fe] focus-visible:ring-offset-2',
				isOpen && 'bg-gray-100 text-[#0080fe]',
				className,
			)}
		>
			<MessageCircle className="w-5 h-5" />
			{unreadCount > 0 && (
				<span
					aria-hidden="true"
					className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center ring-2 ring-white"
				>
					{unreadCount > 9 ? '9+' : unreadCount}
				</span>
			)}
			{/* Announced separately so the count reaches screen readers on change
			    without the button re-reading its whole label. */}
			<span className="sr-only" aria-live="polite">
				{unreadCount > 0 ? `${unreadCount} unread conversations` : ''}
			</span>
		</button>
	)
}

export default ChatPopupTrigger
